import React, { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@material-ui/core";
import { changePassword } from "../app/api/auth";

interface Props {
  open: boolean;
  onClose: () => void;
}

interface FormState {
  email: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const initialForm: FormState = {
  email: "",
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
};

export default function ChangePasswordDialog({ open, onClose }: Props) {
  const [form, setForm] = useState<FormState>(initialForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError(null);
  };

  const handleClose = () => {
    setForm(initialForm);
    setError(null);
    setSuccess(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!form.email || !form.currentPassword || !form.newPassword || !form.confirmPassword) {
      setError("All fields are required.");
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setError("New password and confirm password do not match.");
      return;
    }
    if (form.newPassword.length < 6) {
      setError("New password must be at least 6 characters.");
      return;
    }

    setBusy(true);
    try {
      await changePassword({
        email: form.email,
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      setSuccess(true);
    } catch (err: any) {
      const msg =
        err?.error?.response?.data?.message ||
        "Incorrect email or current password.";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Change Password</DialogTitle>
      <DialogContent>
        {success ? (
          <Typography style={{ padding: "8px 0" }}>
            Password updated successfully. Please log in with your new password.
          </Typography>
        ) : (
          <>
            {error && (
              <Typography color="error" style={{ marginBottom: 8 }}>
                {error}
              </Typography>
            )}
            <TextField
              label="Email"
              name="email"
              value={form.email}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              style={{ marginBottom: 16 }}
            />
            <TextField
              label="Current Password"
              name="currentPassword"
              value={form.currentPassword}
              onChange={handleChange}
              variant="outlined"
              type="password"
              fullWidth
              style={{ marginBottom: 16 }}
            />
            <TextField
              label="New Password"
              name="newPassword"
              value={form.newPassword}
              onChange={handleChange}
              variant="outlined"
              type="password"
              fullWidth
              style={{ marginBottom: 16 }}
            />
            <TextField
              label="Confirm New Password"
              name="confirmPassword"
              value={form.confirmPassword}
              onChange={handleChange}
              variant="outlined"
              type="password"
              fullWidth
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          {success ? "Close" : "Cancel"}
        </Button>
        {!success && (
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={busy}
          >
            {busy ? <CircularProgress size={20} /> : "Update Password"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
